import React from "react";
import { Navigate, Route } from "react-router-dom";
import { PHARMACY_ROLES } from "../constants/roles";
import AppShell from "../layout/AppShell";
// Shared with the ops console -- see src/v2/layout/RequireRoles.jsx.
import RequireRoles from "../layout/RequireRoles";
import { PHARMACY_NAV } from "./nav-config";
import PharmacyOrdersPage from "./pages/orders";
import PharmacyQuotationsPage from "./pages/quotations";
// Default export, unlike the two legacy pages below -- src/pages/home/index.jsx does
// `export default Home`. A named import here yields undefined and crashes.
import Home from "../../pages/home";
// Named export, not default -- src/pages/future-orders does `export { FutureOrders }`.
// A default import here yields undefined and crashes at render.
import { FutureOrders } from "../../pages/future-orders";
// Users + User Details — the legacy /users and /user/:id screens, mounted unchanged in
// every console. The users table links to `<current path>/:id`, so a row click stays in
// this console instead of jumping to the legacy /user/:id route.
import { Users } from "../../pages/users";
import UserDetails from "../../pages/user-details";
// Action Needed — the legacy /action-needed page (orders failing a pre-dispatch guardrail),
// mounted unchanged. API: GET /userOrder/admin/actionNeeded.
import ActionNeeded from "../../pages/action-needed";
// GLP-1 Dashboard — the legacy /glp1-dashboard page, mounted unchanged.
// API: GET /glp1Dashboard/admin/orders (+ POST /delivery/markDelivered/:id from its rows).
import GLP1Dashboard from "../../pages/glp1-dashboard";
// Order Tracking Sheet — the legacy /order-tracking-sheet page, mounted unchanged. Its API
// (pillup-api routes/api/ops/trackingSheet.js TRACKING_SHEET_ROLES) already grants every
// role this console admits.
import OrderTrackingSheet from "../../pages/order-tracking-sheet";

// Pharmacist Console. Orders and Quotations render thin pages built on the SAME legacy
// orderTable/quotationsTable stacks the ops console's own Orders/Quotations screens use
// (see ./pages/orders, ./pages/quotations, and src/v2/ops/pages/orders-legacy for the
// precedent) rather than a separate v2-native implementation -- one table renderer per
// domain, not two that can drift. Reorders renders legacy FutureOrders directly, same
// as /v2/ops/reorders. There is no mock layer anywhere here: every screen reads and
// writes the live database.
//
// Orders is the landing screen rather than Overview: this console is a work queue, not a
// reporting surface, and the prototype's own default section is "orders".
export const PHARMACY_ROUTES = (
    <Route path="/v2/pharmacy" element={<AppShell nav={PHARMACY_NAV} />}>
        <Route index element={<Navigate to="orders" replace />} />
        <Route
            path="overview"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    {/* Same legacy /home charts the ops console's Overview renders --
                        see src/v2/ops/routes.jsx for the OrdersCount-is-live,
                        Cohort-is-a-static-snapshot caveat that carries over unchanged. */}
                    <Home />
                </RequireRoles>
            }
        />
        <Route
            path="orders"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    <PharmacyOrdersPage />
                </RequireRoles>
            }
        />
        <Route
            path="quotations"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    <PharmacyQuotationsPage />
                </RequireRoles>
            }
        />
        <Route
            path="reorders"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    {/* Renders legacy FutureOrders directly, exactly as
                        /v2/ops/reorders does (src/v2/ops/routes.jsx) -- one
                        implementation of the reorder flow, not a second, thinner one.
                        ops-v2-table-refined is the same opt-in column-spacing class
                        the ops route applies. */}
                    <div className="ops-v2-table-refined h-full">
                        <FutureOrders />
                    </div>
                </RequireRoles>
            }
        />
        {/* Inventory and Procurement moved to their own console at /v2/inventory
            (2026-09-30). They were sidebar entries here only because that is where they
            were first built; SUPER_INVENTORY exists now and could reach neither, because
            both were gated on PHARMACY_ROLES. */}
        <Route
            path="tracking-sheet"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    <OrderTrackingSheet />
                </RequireRoles>
            }
        />
        <Route
            path="action-needed"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    <ActionNeeded />
                </RequireRoles>
            }
        />
        <Route
            path="glp1"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    <GLP1Dashboard />
                </RequireRoles>
            }
        />
        <Route
            path="users"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    <Users />
                </RequireRoles>
            }
        />
        <Route
            path="users/:id"
            element={
                <RequireRoles roles={PHARMACY_ROLES}>
                    <UserDetails />
                </RequireRoles>
            }
        />
    </Route>
);
